import { Box, Typography, IconButton, Chip, Grid, Card, CardContent, Divider, CircularProgress, Alert, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGetMaterialReqDetails } from "../hooks/useGetMaterialReqDetails";
import { CalendarToday, Inventory, Person } from "@mui/icons-material";
import { DetailItem } from "../../resourceRequests/components/RequestDetails";

export const RequestDetailsHeader = ({
    projectId,
    requestId,
    onClose
}: {
    projectId: string | undefined;
    requestId: string | null;
    onClose: () => void;
}) => {

    const { data, isFetching, isPending } = useGetMaterialReqDetails({ projectId, requestId });

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase().replace(/_/g, ' ')) {
            case 'order placed':
                return 'primary';
            case 'delivered':
                return 'success';
            case 'pending':
                return 'warning';
            case 'cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2.5,
                borderBottom: 1,
                borderColor: 'divider',
                width: '100%',
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{ flex: 1 }}>
                {(isFetching || isPending) ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography variant="body1">Loading request details...</Typography>
                    </Stack>
                ) : (
                    <Stack spacing={1}>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                            {data?.material_ref}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <Chip
                                label={data?.type_display_text || 'N/A'}
                                size="small"
                                variant="outlined"
                            />
                            <Chip
                                label={data?.status_display_text || 'N/A'}
                                color={getStatusColor(data?.status_display_text || '')}
                                size="small"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Person sx={{ fontSize: 14 }} />
                                {data?.owner_name || 'N/A'}
                            </Typography>
                        </Stack>
                    </Stack>
                )}
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
                <CloseIcon />
            </IconButton>
        </Box>
    );

};

export const RequestDetails = ({ projectId, requestId }: { projectId: string | undefined, requestId: string | null }) => {

    const { data, isFetching, isPending, isError, error } = useGetMaterialReqDetails({ projectId, requestId });

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    if (isFetching || isPending) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">
                    {error?.message || 'Failed to load material request details'}
                </Alert>
            </Box>
        );
    }

    if (!data) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">No data available</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: 'background.default' }}>

            <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <Inventory color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1">General Information</Typography>
                    </Stack>
                    <Divider sx={{ mb: 1 }} />
                    <Stack>
                        <DetailItem label="Material Reference" value={data.material_ref} />
                        <DetailItem label="Type" value={data.type_display_text} />
                        <DetailItem label="Status" value={data.status_display_text} />
                        <DetailItem label="Description" value={data.description} />
                        <DetailItem label="Quantity" value={data.quantity} />
                        <DetailItem label="Owner" value={data.owner_name} />
                    </Stack>
                    <Typography variant="subtitle1" my={1}>Project Details</Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Stack spacing={0.5}>
                        <DetailItem label="Project" value={data.project_title} />
                        <DetailItem label="Phase" value={data.phase_name} />
                        <DetailItem label="Activity" value={data.activity_name} />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" my={1}>
                        <CalendarToday color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1">Supplier & Delivery</Typography>
                    </Stack>
                    <Divider sx={{ mb: 1 }} />
                    <Stack spacing={0.5}>
                        <DetailItem label="Supplier" value={data.supplier_display_text} />
                        <DetailItem label="Point of Contact" value={data.point_of_contact_info} />
                        <DetailItem label="Storage Location" value={data.storage_location} />
                        <DetailItem label="Required Date" value={formatDate(data.required_date)} />
                        <DetailItem label="Order Placed Date" value={formatDate(data.order_placed_date)} />
                        <DetailItem label="Est. Delivery Date" value={formatDate(data.est_delivery_date)} />
                        <DetailItem label="Last Updated Date" value={formatDate(data.last_updated_date)} />
                        <DetailItem label="PO Number" value={data.po_number} />
                        <DetailItem label="Date PO Raised" value={formatDate(data.date_po_raised)} />
                        <DetailItem label="Created Date" value={formatDate(data.created_date)} />
                        <DetailItem label="Updated Date" value={formatDate(data.updated_date)} />
                    </Stack>
                    <Typography variant="subtitle1" my={1}>Additional Information</Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} display="block" mb={1}>
                            Last Updated Comments
                        </Typography>
                        <Typography variant="body2">
                            {data.last_updated_comments || 'No comments available'}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

        </Box>
    );

};